import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Printer } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  OVERALL_REPORT_LINES,
  OVERALL_REPORT_LINE_ROLES,
  type OverallReportBreakdownLine,
  type OverallReportDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { ReportRangePicker } from './ReportRangePicker';
import {
  downloadReportExport,
  matchPreset,
  rangeFromBusinessDay,
  rangeFromPreset,
  useOverallReport,
  useReportCatalogue,
  type ReportPreset,
  type ReportRangeState,
} from './useReports';

/**
 * Overall Report (SPEC §5.7) — its own full page.
 *
 * Left panel: Reservation Sales broken down by device category, with the Cafe
 * Sales breakdown beneath it. Right panel: the ten total lines in the order the
 * spec prescribes, ending in a bold Net Profit. Refill is NET of balance
 * refunds, and the two figures behind it - "Topped up" and "Refunded" - sit
 * directly under it as information, so it reads as Topped up - Refunded
 * rather than leaving the refund among the deductions (owner, 2026-10-14).
 *
 * The line order comes from `OVERALL_REPORT_LINES` rather than being written
 * out here, so it cannot be reshuffled by an accident of object key order, and
 * the arithmetic roles come from the same contract the server computes against
 * — plus the service charge, spliced in after Cafe Sales (see
 * {@link TOTALS_PANEL_LINES}).
 */
export function OverallReportPage() {
  const { t, i18n } = useTranslation();
  const { can, venue } = useAuth();
  const { catalogue } = useReportCatalogue();

  const [draftRange, setDraftRange] = useState<ReportRangeState | null>(null);
  const [appliedRange, setAppliedRange] = useState<ReportRangeState | null>(null);
  const [exporting, setExporting] = useState<null | 'csv' | 'pdf'>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Defaults to the business day currently in progress (§5.7).
  useEffect(() => {
    if (!catalogue || draftRange) return;
    const seeded = rangeFromBusinessDay(
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
    );
    setDraftRange(seeded);
    setAppliedRange(seeded);
  }, [catalogue, draftRange]);

  const applyPreset = (preset: ReportPreset) => {
    if (!catalogue) return;
    const next = rangeFromPreset(
      preset,
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
      catalogue.earliestBusinessDate,
    );
    setDraftRange(next);
    setAppliedRange(next);
  };

  const { report, loading, error } = useOverallReport(appliedRange);
  const currencySymbol = report?.currencySymbol ?? venue?.currencySymbol ?? 'IQD';

  const money = (millidinars: string) =>
    formatIqd(fromMillidinars(millidinars), { currencySymbol, locale: i18n.language });

  async function handleExport(format: 'csv' | 'pdf'): Promise<void> {
    if (!appliedRange) return;
    setExporting(format);
    setExportError(null);
    try {
      await downloadReportExport('/reports/overall/export', appliedRange, format);
    } catch (caught) {
      setExportError(caught instanceof Error ? caught.message : 'Export failed.');
    } finally {
      setExporting(null);
    }
  }

  const canExport = can('reports.export');

  if (!can('reports.view')) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('reports.overallReport')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('reports.noViewPermission')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .gc-overall-print, .gc-overall-print * { visibility: visible !important; }
          .gc-overall-print {
            position: absolute !important;
            inset-inline-start: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
          .gc-no-print { display: none !important; }
        }
      `}</style>

      <header className="gc-no-print flex flex-wrap items-center gap-3 px-4 pt-4">
        <Link
          to="/reports"
          aria-label={t('reports.backToReports')}
          className="grid h-8 w-8 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </Link>
        <h1 className="text-xl font-semibold text-content">{t('reports.overallReport')}</h1>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => window.print()}
          disabled={!report}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:opacity-40"
        >
          <Printer className="h-4 w-4" aria-hidden />
          {t('common.print')}
        </button>
        <button
          type="button"
          onClick={() => void handleExport('csv')}
          disabled={!report || !canExport || exporting !== null}
          title={canExport ? undefined : t('reports.noExportPermission')}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-4 w-4" aria-hidden />
          {t('reports.exportCsv')}
        </button>
        <button
          type="button"
          onClick={() => void handleExport('pdf')}
          disabled={!report || !canExport || exporting !== null}
          title={canExport ? undefined : t('reports.noExportPermission')}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FileText className="h-4 w-4" aria-hidden />
          {t('reports.exportPdf')}
        </button>
      </header>

      {draftRange ? (
        <div className="gc-no-print px-4 pt-4">
          <ReportRangePicker
            value={draftRange}
            onChange={setDraftRange}
            onFetch={() => setAppliedRange(draftRange)}
            busy={loading}
            resolved={report?.range ?? null}
            onPreset={catalogue ? applyPreset : undefined}
            activePreset={catalogue ? matchPreset(draftRange, catalogue) : null}
          />
        </div>
      ) : null}

      {exportError ? (
        <p role="alert" className="gc-no-print px-4 pt-2 text-sm text-danger">
          {exportError}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="p-4 text-sm text-danger">
          {error}
        </p>
      ) : loading && !report ? (
        <p className="p-4 text-sm text-content-muted">{t('common.loading')}</p>
      ) : report ? (
        <div className="gc-overall-print grid flex-1 grid-cols-1 gap-4 p-4 lg:grid-cols-[1fr_22rem]">
          <div className="space-y-4">
            <BreakdownPanel
              title={t('reports.overall.reservationSales')}
              lines={report.reservationSalesByCategory}
              totalMd={report.totals.reservationSalesMd}
              money={money}
              emptyLabel={t('reports.overall.noSales')}
              unassignedLabel={t('reports.overall.unassigned')}
            />
            <BreakdownPanel
              title={t('reports.overall.cafeSales')}
              lines={report.cafeSalesByCategory}
              totalMd={report.totals.cafeSalesMd}
              money={money}
              emptyLabel={t('reports.overall.noSales')}
              unassignedLabel={t('reports.overall.unassigned')}
            />
          </div>

          <TotalsPanel report={report} money={money} />
        </div>
      ) : null}
    </div>
  );
}

/** A category panel with a proportional bar per line (SPEC §5.7 left panel). */
function BreakdownPanel({
  title,
  lines,
  totalMd,
  money,
  emptyLabel,
  unassignedLabel,
}: {
  title: string;
  lines: OverallReportBreakdownLine[];
  totalMd: string;
  money: (millidinars: string) => string;
  emptyLabel: string;
  unassignedLabel: string;
}) {
  return (
    <section className="rounded-card border border-subtle bg-raised p-4">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-content">{title}</h2>
        <span className="tabular text-base font-semibold text-content">{money(totalMd)}</span>
      </header>

      {lines.length === 0 ? (
        <p className="text-sm text-content-muted">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2.5">
          {lines.map((line) => (
            <li key={line.id}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-content-secondary">{line.name || unassignedLabel}</span>
                <span className="tabular text-content">{money(line.amountMd)}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-input">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ inlineSize: `${line.sharePercent}%` }}
                  // The bar is decoration; the figure beside it is the datum.
                  aria-hidden
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The right panel's lines: the spec's ten, with the service charge after Cafe
 * Sales (owner, 2026-09-23). It counts into Incomes — the server adds
 * `totals.serviceChargeMd` there — so a panel without it would show an Incomes
 * figure its own lines do not add up to. Spliced in here because
 * `OVERALL_REPORT_LINES` lives in the frozen shared package; the server's
 * exports splice it the same way.
 */
type TotalsPanelLine = (typeof OVERALL_REPORT_LINES)[number] | 'serviceCharge';
export const TOTALS_PANEL_LINES: readonly TotalsPanelLine[] = OVERALL_REPORT_LINES.flatMap(
  (line): TotalsPanelLine[] => (line === 'cafeSales' ? [line, 'serviceCharge'] : [line]),
);

/**
 * The right panel: the spec's lines in order, ending in a bold Net Profit.
 *
 * Roles decide the styling (see `OVERALL_REPORT_LINE_ROLES`): a `deduction`
 * carries a "−" and the danger tone, a `memo` says so, and a `detail` - Topped
 * up and Refunded - is indented and muted, because both are already inside the
 * Refill line above them and nothing is added or taken off for them again.
 */
export function TotalsPanel({
  report,
  money,
}: {
  report: OverallReportDto;
  money: (millidinars: string) => string;
}) {
  const { t } = useTranslation();

  return (
    <section className="h-fit rounded-card border border-subtle bg-raised p-4">
      <h2 className="mb-3 text-base font-semibold text-content">{t('common.total')}</h2>

      <dl className="space-y-1">
        {TOTALS_PANEL_LINES.map((line) => {
          const role = line === 'serviceCharge' ? 'revenue' : OVERALL_REPORT_LINE_ROLES[line];
          // `?? '0'`: a report cached from before the charge existed has no key.
          const amount = report.totals[`${line}Md` as keyof typeof report.totals] ?? '0';
          const isResult = role === 'result';
          const isSubtotal = role === 'subtotal';
          // Topped up and Refunded: the figures behind the Refill line above,
          // already INSIDE it. Indented and quiet so they read as information,
          // never as something added or taken off a second time (owner,
          // 2026-10-14). Neither carries a "−": Refunded is shown as a plain
          // amount, the arithmetic being Refill = Topped up - Refunded.
          const isDetail = role === 'detail';

          return (
            <div
              key={line}
              className={`flex items-baseline justify-between gap-3 py-1 ${
                isSubtotal || isResult ? 'mt-2 border-t border-subtle pt-2' : ''
              }`}
            >
              <dt
                className={
                  isResult
                    ? 'font-semibold text-content'
                    : isSubtotal
                      ? 'font-medium text-content'
                      : isDetail
                        ? 'ps-4 text-xs text-content-muted'
                        : 'text-sm text-content-secondary'
                }
              >
                {t(`reports.overall.${line}`)}
                {/* A recharge bonus is balance given away, not cash taken; it
                    sits in neither subtotal, so say so instead of leaving the
                    reader to wonder why the column does not add up. */}
                {role === 'memo' ? (
                  <span className="ms-1 text-xs text-content-muted">
                    ({t('reports.overall.memo')})
                  </span>
                ) : null}
                {role === 'deduction' ? (
                  <span className="ms-1 text-xs text-content-muted">−</span>
                ) : null}
              </dt>
              <dd
                className={`tabular ${
                  isResult
                    ? // Net Profit in the current, glowing (card `.kv.grand b`)
                      // — the figure the whole report resolves to.
                      'text-base font-bold text-primary [text-shadow:var(--glow-primary)]'
                    : isSubtotal
                      ? 'font-medium text-content'
                      : role === 'deduction'
                        ? 'text-sm text-danger'
                        : isDetail
                          ? 'text-xs text-content-muted'
                          : 'text-sm text-content'
                }`}
              >
                {money(amount)}
              </dd>
            </div>
          );
        })}
      </dl>

      <p className="mt-3 border-t border-subtle pt-2 text-xs text-content-muted">
        {t('reports.overall.formula')}
      </p>
    </section>
  );
}
